import { format, formatDistanceToNowStrict, intervalToDuration, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import humanizeDuration from 'humanize-duration';

export const COMMON_TIME_FORMAT = 'HH:mm';
export const COMMON_DATE_FORMAT = 'MMM do yyyy';
export const COMMON_DATE_TIME_FORMAT = `${COMMON_DATE_FORMAT} 'at' ${COMMON_TIME_FORMAT}`;
export const COMMON_LONG_DATE_FORMAT = `MMMM d' 'yyyy`;
export const COMMON_DATE_TIME_FORMAT_WITH_SECONDS = `${COMMON_LONG_DATE_FORMAT} 'at' ${COMMON_TIME_FORMAT}:ss`;

type AcceptedDateFormat = Date | string | number;

interface DateTimeFormatOptions {
  /**
   * Whether to append the timezone to the formatted string. E.g. `17:52 (BST)`. Defaults to `false`.
   */
  appendTimeZone?: boolean;
  /**
   * Override the timezone used to the specified time zone name. **You will need to set `appendTimeZone` to true for the timezone to be displayed.**
   * E.g. `Europe/London`.
   * (https://en.wikipedia.org/wiki/List_of_tz_database_time_zones)
   */
  timeZoneOverride?: string;
}

const timeLeftZeroPad = (num: number) => num.toString().padStart(2, '0');
const getFallbackGmtOffset = () => {
  const offset = new Date().getTimezoneOffset();
  if (offset === 0) return 'GMT';
  const offsetDuration = intervalToDuration({
    start: 0,
    end: Math.abs(offset) * 60 * 1000,
  });
  const offsetSign = offset > 0 ? '+' : '-';
  const offsetMinutes = offsetDuration.minutes ? `:${timeLeftZeroPad(offsetDuration.minutes)}` : '';
  return `GMT${offsetSign}${offsetDuration.hours ?? 0}${offsetMinutes}`;
};

const getFriendlyTimeZoneName = (timeZone?: string) => {
  const dateTimeFormatInstance =
    typeof Intl === 'undefined'
      ? undefined
      : Intl?.DateTimeFormat(undefined, {
          timeZoneName: 'short',
          timeZone,
        });
  const shortIntlTimeZoneName = dateTimeFormatInstance
    ?.formatToParts?.(new Date())
    ?.find((part) => part.type === 'timeZoneName')?.value;
  if (shortIntlTimeZoneName) {
    return shortIntlTimeZoneName;
  }
  return getFallbackGmtOffset();
};
export const FRIENDLY_BROWSER_TIME_ZONE_NAME = getFriendlyTimeZoneName();
export const addTimeZoneToDateFnsFormatString = (formatString: string, timezoneText?: string) =>
  `${formatString}' (${timezoneText ?? FRIENDLY_BROWSER_TIME_ZONE_NAME})'`;

const curriedFormat =
  (formatString: string) =>
  /**
   * @param date - **Browser time zone** Date object or **UTC** timestamp or a valid ISO 8601 string
   * @param options - Optional formatting options
   */
  (date: AcceptedDateFormat, options?: DateTimeFormatOptions) => {
    const timeZoneOverride = options?.timeZoneOverride;
    const appendTimeZone = options?.appendTimeZone;

    const dateObjectOrTimestamp = typeof date === 'string' ? parseISO(date) : date;

    let updatedFormatString: string = formatString;
    if (appendTimeZone) {
      updatedFormatString = addTimeZoneToDateFnsFormatString(updatedFormatString, timeZoneOverride);
    }

    if (timeZoneOverride) {
      return formatInTimeZone(dateObjectOrTimestamp, timeZoneOverride, updatedFormatString);
    }
    return format(dateObjectOrTimestamp, updatedFormatString);
  };

export const textForDifferenceInDays = (date: string | Date) => {
  const dateObjectOrTimestamp = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNowStrict(dateObjectOrTimestamp, { addSuffix: true, unit: 'day' });
};

export const formatDateTimeWithSeconds = curriedFormat(COMMON_DATE_TIME_FORMAT_WITH_SECONDS);

export const formatDate = curriedFormat(COMMON_DATE_FORMAT);
export const formatTime = curriedFormat(COMMON_TIME_FORMAT);
export const formatDateTime = curriedFormat(COMMON_DATE_TIME_FORMAT);

/**
 * A helper to provide a set of shorter strings for humanizing durations
 * Primarily for use with tables where the full date-fns formatDistance string is not appropriate
 */
export const shortEnglishHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mon',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
});
